import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import type { QuoteFormData } from '../state/types';
import { submitQuote } from '../api/client';

/**
 * Step 8 – Customer Info: collects contact details and submits the quote.
 */
export class StepCustomerInfo implements BuilderStep {
  readonly id = 'customer-info';
  readonly label = 'Your Info';

  private container: HTMLElement | null = null;
  private form: HTMLFormElement | null = null;

  constructor(
    private store: BuilderStore,
    public readonly config: BootstrapConfig
  ) { }

  isEnabled(_config: BootstrapConfig): boolean {
    return true;
  }

  render(container: HTMLElement): void {
    this.container = container;
    const state = this.store.getState();
    const q = state.quoteData;

    container.innerHTML = `
      <div class="vc-step vc-step-customer-info">
        <h2>Your Information</h2>
        <p class="vc-step-desc">Complete the form below to receive your personalized quote.</p>

        <form id="vc-customer-form" class="vc-form" novalidate>
          <div class="vc-form-row">
            <div class="vc-form-group">
              <label for="vc-first-name">First Name *</label>
              <input type="text" id="vc-first-name" name="firstName" class="vc-input" required value="${q.firstName ?? ''}" />
              <span class="vc-field-error" data-field="firstName"></span>
            </div>
            <div class="vc-form-group">
              <label for="vc-last-name">Last Name *</label>
              <input type="text" id="vc-last-name" name="lastName" class="vc-input" required value="${q.lastName ?? ''}" />
              <span class="vc-field-error" data-field="lastName"></span>
            </div>
          </div>

          <div class="vc-form-row">
            <div class="vc-form-group">
              <label for="vc-email">Email *</label>
              <input type="email" id="vc-email" name="email" class="vc-input" required value="${q.email ?? ''}" />
              <span class="vc-field-error" data-field="email"></span>
            </div>
            <div class="vc-form-group">
              <label for="vc-phone">Phone</label>
              <input type="tel" id="vc-phone" name="phone" class="vc-input" value="${q.phone ?? ''}" />
              <span class="vc-field-error" data-field="phone"></span>
            </div>
          </div>

          <div class="vc-form-group">
            <label for="vc-address">Address</label>
            <input type="text" id="vc-address" name="address" class="vc-input" value="${q.address ?? ''}" />
          </div>

          <div class="vc-form-row vc-form-row-3">
            <div class="vc-form-group">
              <label for="vc-city">City</label>
              <input type="text" id="vc-city" name="city" class="vc-input" value="${q.city ?? ''}" />
            </div>
            <div class="vc-form-group">
              <label for="vc-state">State/Province</label>
              <input type="text" id="vc-state" name="state" class="vc-input" value="${q.state ?? ''}" />
            </div>
            <div class="vc-form-group">
              <label for="vc-postal">Postal Code</label>
              <input type="text" id="vc-postal" name="postalCode" class="vc-input" value="${q.postalCode ?? ''}" />
            </div>
          </div>

          <div class="vc-form-row">
            <div class="vc-form-group">
              <label for="vc-country">Country</label>
              <select id="vc-country" name="country" class="vc-select">
                <option value="US" ${q.country === 'US' || !q.country ? 'selected' : ''}>United States</option>
                <option value="CA" ${q.country === 'CA' ? 'selected' : ''}>Canada</option>
                <option value="other" ${q.country === 'other' ? 'selected' : ''}>Other</option>
              </select>
            </div>
            <div class="vc-form-group">
              <label for="vc-how">How did you hear about us?</label>
              <select id="vc-how" name="howDidYouHear" class="vc-select">
                <option value="">— Select —</option>
                <option value="search" ${q.howDidYouHear === 'search' ? 'selected' : ''}>Search Engine</option>
                <option value="social" ${q.howDidYouHear === 'social' ? 'selected' : ''}>Social Media</option>
                <option value="friend" ${q.howDidYouHear === 'friend' ? 'selected' : ''}>Friend / Referral</option>
                <option value="show" ${q.howDidYouHear === 'show' ? 'selected' : ''}>Boat Show</option>
                <option value="dealer" ${q.howDidYouHear === 'dealer' ? 'selected' : ''}>Dealer</option>
                <option value="other" ${q.howDidYouHear === 'other' ? 'selected' : ''}>Other</option>
              </select>
            </div>
          </div>

          <div class="vc-form-group">
            <label for="vc-comments">Comments</label>
            <textarea id="vc-comments" name="comments" class="vc-textarea" rows="3">${q.comments ?? ''}</textarea>
          </div>

          <div id="vc-form-message" class="vc-form-message"></div>

          <div class="vc-form-actions">
            <button type="submit" class="vc-btn vc-btn-primary" id="vc-submit-btn">
              Submit Quote Request
            </button>
          </div>
        </form>
      </div>
    `;

    this.form = container.querySelector('#vc-customer-form') as HTMLFormElement;
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Save form data to store on input change
    this.form?.addEventListener('input', () => this.syncFormToStore());
  }

  validate(): boolean {
    const state = this.store.getState();
    const q = state.quoteData;
    return !!(q.firstName && q.lastName && q.email);
  }

  onEnter(): void { }

  onLeave(): void {
    this.syncFormToStore();
  }

  destroy(): void {
    this.container = null;
    this.form = null;
  }

  // ── private ─────────────────────────────────────────────

  private syncFormToStore(): void {
    if (!this.form) return;
    const fd = new FormData(this.form);
    const quoteData: Partial<QuoteFormData> = {
      firstName: (fd.get('firstName') as string) ?? '',
      lastName: (fd.get('lastName') as string) ?? '',
      email: (fd.get('email') as string) ?? '',
      phone: (fd.get('phone') as string) ?? '',
      address: (fd.get('address') as string) ?? '',
      city: (fd.get('city') as string) ?? '',
      state: (fd.get('state') as string) ?? '',
      postalCode: (fd.get('postalCode') as string) ?? '',
      country: (fd.get('country') as string) ?? '',
      comments: (fd.get('comments') as string) ?? '',
      howDidYouHear: (fd.get('howDidYouHear') as string) ?? '',
    };
    this.store.setState({ quoteData });
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    this.syncFormToStore();

    const msgEl = this.container?.querySelector('#vc-form-message');
    const submitBtn = this.container?.querySelector('#vc-submit-btn') as HTMLButtonElement | null;

    // Clear previous errors
    this.container?.querySelectorAll('.vc-field-error').forEach(el => (el.textContent = ''));
    if (msgEl) msgEl.innerHTML = '';

    // Client-side validation
    const errors = this.validateForm();
    if (errors.length > 0) {
      errors.forEach(err => {
        const fieldErr = this.container?.querySelector(`.vc-field-error[data-field="${err.field}"]`);
        if (fieldErr) fieldErr.textContent = err.message;
      });
      return;
    }

    // Build quote payload — API expects flat snake_case customer fields + quote_json snapshot
    const state = this.store.getState();
    const customer = state.quoteData;

    const quoteJson = {
      engine_id: state.selectedEngineId,
      selected_colors: state.selectedColors,
      selected_options: state.selectedOptions,
    };

    const payload = {
      model_id: state.selectedModelId,
      dealer_id: state.selectedDealerId ?? null,
      first_name: customer.firstName ?? '',
      last_name: customer.lastName ?? '',
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      address: customer.address ?? '',
      city: customer.city ?? '',
      state: customer.state ?? '',
      postal_code: customer.postalCode ?? '',
      country: customer.country || 'US',
      comments: customer.comments ?? '',
      how_did_you_hear: customer.howDidYouHear ?? '',
      quote_json: quoteJson,
      msrp_total: state.quoteTotals?.msrp ?? 0,
      sale_total: state.quoteTotals?.salePrice ?? 0,
      dealer_net_total: state.quoteTotals?.dealerNet ?? 0,
    };

    // Disable button during submission
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';
    }

    try {
      await submitQuote(payload);

      if (msgEl) {
        const customMsg = typeof this.config.settings?.quoteThankYouMessage === 'string'
          ? this.config.settings.quoteThankYouMessage.trim()
          : '';
        const bodyHtml = customMsg
          ? customMsg.replace(/\r?\n/g, '<br>')
          : `A copy of your quote has been sent to <strong>${state.quoteData.email}</strong>.<br>A representative will be in touch shortly.`;
        msgEl.innerHTML = `
          <div class="vc-success">
            <h3>Quote Submitted Successfully!</h3>
            <p>${bodyHtml}</p>
          </div>
        `;
      }

      // Hide the form
      if (this.form) this.form.style.display = 'none';
    } catch (err) {
      if (msgEl) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        msgEl.innerHTML = `<div class="vc-error">${message}</div>`;
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit Quote Request';
      }
    }
  }

  private validateForm(): { field: string; message: string }[] {
    const errors: { field: string; message: string }[] = [];
    const q = this.store.getState().quoteData;

    if (!q.firstName?.trim()) {
      errors.push({ field: 'firstName', message: 'First name is required.' });
    }
    if (!q.lastName?.trim()) {
      errors.push({ field: 'lastName', message: 'Last name is required.' });
    }
    if (!q.email?.trim()) {
      errors.push({ field: 'email', message: 'Email is required.' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.email)) {
      errors.push({ field: 'email', message: 'Please enter a valid email.' });
    }

    return errors;
  }
}
